/**
 * Responsibility: the sports and TCG arbitrage board.
 *
 * Deliberately not the same table as the Pokemon board. That one has a
 * gem rate from a population report and can say what a submission
 * returns on average; this universe has no population behind it, so every
 * figure here is what one copy would pay if it graded perfectly. The
 * column is called "If one gems" for that reason and there is no average
 * column to mistake it for.
 *
 * The grade 9 price earns its column because it is the evidence. A PSA 10
 * price with no Grade 9 under it is a number nothing has traded at, and
 * seeing the ladder is how a reader judges whether the gem price is real.
 */
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import FavoriteIcon from '@mui/icons-material/Favorite';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { ToolbarIconButton } from '@kit/components/buttons';
import { DataTable } from '@kit/components/data';
import type { DataColumn } from '@kit/components/data';
import type { CatalogueRow } from '../../api/client';
import { CardThumb } from './CardThumb';
import { compactMoney, money, multiple } from './money';

const THUMB_WIDTH = 46;

type CatalogueTableProps = {
  rows: CatalogueRow[];
  loading: boolean;
  onOpen: (row: CatalogueRow) => void;
  /** Keep a card to look at again. */
  onLike: (row: CatalogueRow) => void;
  /** Track a card, to see what it does next. */
  onWatch: (row: CatalogueRow) => void;
  liked: Set<string>;
  watched: Set<string>;
  maxHeight?: number;
};

/**
 * Column widths are measured, not guessed: headings never wrap, so a
 * heading that does not fit its column is clipped. `tests/table-headers.mjs`
 * renders this table in a browser and fails on a clipped one.
 */
export function CatalogueTable({
  rows,
  loading,
  onOpen,
  onLike,
  onWatch,
  liked,
  watched,
  maxHeight,
}: CatalogueTableProps) {
  const columns: DataColumn<CatalogueRow>[] = [
    {
      id: 'card',
      label: 'Card',
      render: (row) => (
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0, py: 0.5 }}>
          <CardThumb src={row.image_url} alt={row.name} width={THUMB_WIDTH} />
          <Stack spacing={0.25} sx={{ minWidth: 0 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
              {row.name}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap>
              {row.set_name}
            </Typography>
          </Stack>
        </Stack>
      ),
      sortValue: (row) => row.name,
    },
    {
      id: 'raw',
      label: 'Raw',
      width: 100,
      align: 'right',
      render: (row) => (
        <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums' }}>
          {money(row.ungraded)}
        </Typography>
      ),
      sortValue: (row) => Number(row.ungraded),
    },
    {
      id: 'nine',
      label: 'PSA 9',
      width: 105,
      align: 'right',
      render: (row) => (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ fontVariantNumeric: 'tabular-nums' }}
        >
          {compactMoney(row.grade_nine)}
        </Typography>
      ),
      sortValue: (row) => Number(row.grade_nine),
    },
    {
      id: 'gem',
      label: 'PSA 10',
      width: 105,
      align: 'right',
      render: (row) => (
        <Typography
          variant="body2"
          sx={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}
        >
          {compactMoney(row.gem)}
        </Typography>
      ),
      sortValue: (row) => Number(row.gem),
    },
    {
      id: 'multiple',
      label: 'Mult',
      width: 92,
      align: 'right',
      render: (row) => (
        <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums' }}>
          {multiple(row.multiple)}
        </Typography>
      ),
      sortValue: (row) => Number(row.multiple),
    },
    {
      id: 'cost',
      label: 'All in',
      width: 92,
      align: 'right',
      render: (row) => (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ fontVariantNumeric: 'tabular-nums' }}
        >
          {compactMoney(row.total_cost)}
        </Typography>
      ),
      sortValue: (row) => Number(row.total_cost),
    },
    {
      id: 'profit',
      label: 'If gems',
      width: 106,
      align: 'right',
      render: (row) => (
        <Typography
          variant="body2"
          color="success.main"
          sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}
        >
          {compactMoney(row.best_case_profit)}
        </Typography>
      ),
      sortValue: (row) => Number(row.best_case_profit),
    },
    {
      // Last, and narrow: the decisions belong beside the row that prompted
      // them, and a board is read left to right for the numbers first.
      id: 'actions',
      label: '',
      width: 84,
      align: 'right',
      render: (row) => (
        <Stack direction="row" spacing={0.25} justifyContent="flex-end">
          <ToolbarIconButton
            aria-label={
              liked.has(row.product_key) ? 'Remove from likes' : 'Like, to look at again'
            }
            onClick={(event) => {
              event.stopPropagation();
              onLike(row);
            }}
          >
            {liked.has(row.product_key) ? (
              <FavoriteIcon fontSize="small" color="error" />
            ) : (
              <FavoriteBorderIcon fontSize="small" />
            )}
          </ToolbarIconButton>
          <ToolbarIconButton
            aria-label={
              watched.has(row.product_key) ? 'Stop watching this card' : 'Watch this card'
            }
            onClick={(event) => {
              event.stopPropagation();
              onWatch(row);
            }}
          >
            {watched.has(row.product_key) ? (
              <VisibilityIcon fontSize="small" color="primary" />
            ) : (
              <VisibilityOutlinedIcon fontSize="small" />
            )}
          </ToolbarIconButton>
        </Stack>
      ),
    },
    {
      id: 'evidence',
      label: 'Basis',
      width: 92,
      render: () => (
        <Chip size="small" variant="outlined" color="warning" label="list price" />
      ),
    },
  ];

  return (
    <DataTable
      rows={rows}
      columns={columns}
      loading={loading}
      rowKey={(row) => row.product_key}
      onRowClick={onOpen}
      maxHeight={maxHeight}
      emptyMessage="Nothing clears its costs in this catalogue under these filters."
    />
  );
}
